import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import PdfViewDrawer from "../../../../../components/PdfViewDrawer";
import ProfileViewer from "../../../../../components/ProfileViewer";
import Text from "../../../../../components/Text";
import {
  GENDER_RECORD,
  STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT,
  TIME_ZONE_MAPPER,
} from "../../../../../constants";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useProvider } from "../../../../../queries/use-provider";
import { formatDate } from "../../../../../utils/date-time-utility";
import { DEFAULT_USER_SRC } from "../../../../../utils/utils";

const ProviderProfile = ({
  providerData,
  groupNpi,
}: {
  providerData?: any;
  groupNpi?: string;
}) => {
  const params = useParams();
  const { data } = useProvider({
    providerUuid: providerData?.uuid,
    tenantId: params.providerGroupId,
  });

  const insurances = providerData?.acceptedInsurances
    ? Object.values(providerData?.acceptedInsurances)?.map(
        (it, ind) => (ind + 1)?.toString() + ". " + it?.toString()
      )
    : "-";

  const workLocations = Object?.keys(providerData?.workLocations ?? {})?.length
    ? providerData?.workLocations
    : "-";

  const [previewResultDisc] = useReduceDisclosure(2);
  return (
    <Flex gap={"10px"} flexDirection={"column"}>
      <OutlineCard>
        <Flex mx={"12px"} my={"24px"}>
          <Grid width={"24%"}>
            <Avatar
              sx={{
                width: "140px",
                height: "135px",
              }}
              src={(data?.data as any)?.avatar ?? DEFAULT_USER_SRC}
            />
            <Text mt={"16px"} size={"16px"} fontWeight={"bold"}>
              {(providerData?.firstName ?? "") +
                " " +
                (providerData?.lastName ?? "")}
            </Text>
          </Grid>
          <Grid container columns={3}>
            <Grid item xs={1}>
              <ProfileViewer
                withoutColn
                gap={"16px"}
                firstColW={4}
                secondColW={8}
                data={{
                  provider_type: providerData?.providerType,
                  contact_number: providerData?.phone,
                }}
              />
            </Grid>
            <Grid item xs={1}>
              <ProfileViewer
                withoutColn
                gap={"16px"}
                firstColW={2}
                secondColW={8}
                data={{
                  gender: GENDER_RECORD[providerData?.gender] || "",
                  email: providerData?.email,
                }}
              />
            </Grid>
            <Grid item xs={1}>
              <ProfileViewer
                withoutColn
                gap={"16px"}
                firstColW={4}
                secondColW={7.5}
                data={{
                  DOB: formatDate(providerData?.birthDate) ?? "",
                  "NPI Number": providerData?.npi?.toString() || "",
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <ProfileViewer
                withoutColn
                gap={"16px"}
                firstColW={1.4}
                secondColW={7.5}
                data={{
                  bio: (data?.data as any)?.bio,
                }}
              />
            </Grid>
          </Grid>
        </Flex>
      </OutlineCard>
      <OutlineCard>
        <Box my={"21px"} mx={"16px"}>
          <Text variant="h4" fontWeight={"medium"}>
            BASIC INFORMATION
          </Text>
          <Flex mt={"27px"}>
            <Box width={"50%"}>
              <ProfileViewer
                gap={"14px"}
                withoutColn
                firstColW={3.5}
                secondColW={8}
                data={{
                  "Office Fax Number": providerData?.officeFaxNumber,
                  "Group NPI Number":
                    groupNpi ?? providerData?.groupNpiNumber?.toString(),
                  "Taxonomy Number": providerData?.taxonomyNumber,
                  "Licensed Details":
                    data?.data?.licenceInformation?.map((lic, index) => {
                      if (
                        !lic.licenseState &&
                        !lic.licenseNumber &&
                        !lic.expiryDate
                      )
                        return "";
                      else
                        return `${index + 1}.${
                          STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[
                            lic.licenseState ?? ""
                          ]
                        } (${lic.licenseNumber})
                                 ${
                                   lic.licenseActiveDate
                                     ? `(${formatDate(lic.licenseActiveDate)})`
                                     : ""
                                 } )
                         ${
                           lic.expiryDate
                             ? `(${formatDate(lic.expiryDate)})`
                             : ""
                         }`;
                    }) ?? [],
                }}
              />
            </Box>
            <Box width={"50%"}>
              <ProfileViewer
                gap={"5px"}
                withoutColn
                firstColW={3.5}
                secondColW={8}
                data={{
                  "Time Zone": TIME_ZONE_MAPPER[providerData?.timezone] || "",
                  "Work Location": workLocations,
                  "Insurance(s) a Accepted": insurances,
                }}
              />
            </Box>
          </Flex>
        </Box>
      </OutlineCard>
      {/* <OutlineCard>
        <Box my={"21px"} mx={"16px"}>
          <Text variant="h4" fontWeight={"medium"}>
            BASIC ACCOUNT PROFILE DATA
          </Text>
          <Flex gap={"10px"} mt={"27px"}>
            <ProfileViewer
              gap={"14px"}
              withoutColn
              firstColW={5.8}
              secondColW={6}
              data={{
                "Sub - Specialties / Areas Of Focus": providerData?.areaFocus,
                "Hospital Affiliation": providerData?.hospitalAffiliation,
                "Year Of Experience": providerData?.experience,
                "Accept New Patients": providerData?.acceptNewPatient
                  ? "Yes"
                  : "No",
              }}
            />
            <Box width={"100%"}>
              <ProfileViewer
                gap={"16px"}
                withoutColn
                firstColW={5.8}
                secondColW={6}
                data={{
                  "Age Group Seen":
                    AGE_GROUP_SEEN_RECORD[providerData?.ageGroupSeen],
                  accept_cash_pay: providerData?.acceptCash ? "Yes" : "No",
                  "Provider Employment": providerData?.providerEmployment,
                }}
              />
            </Box>
          </Flex>
          <Grid container mt={"10px"}>
            <ProfileViewer
              gap={"16px"}
              withoutColn
              firstColW={2.9}
              secondColW={6}
              data={{
                "Work Experience": providerData?.workExperience,
              }}
            />
          </Grid>
        </Box>
      </OutlineCard> */}
      <OutlineCard>
        <Box mx={"16px"}>
          <Accordion
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              sx={{ paddingLeft: 0 }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`dea-info-content`}
              id={`dea-info-header`}
            >
              <Typography fontWeight={500}>DEA Information</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {providerData?.deaInformation &&
                providerData?.deaInformation?.length > 0 &&
                providerData?.deaInformation?.map(
                  (item: any, index: number) => (
                    <Flex mt={"27px"} key={index}>
                      <Box width={"38%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            Name: item?.licenseName ?? "",
                          }}
                        />
                      </Box>
                      <Box width={"40%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            Number: item?.deaNumber ?? "",
                          }}
                        />
                      </Box>
                      <Box width={"35%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            " State":
                              STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[
                                item?.deaState ?? ""
                              ],
                          }}
                        />
                      </Box>
                      <Box width={"38%"}>
                        <ProfileViewer
                          gap={"5px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            "Active Date": item?.deaActiveDate
                              ? (formatDate(item?.deaActiveDate) as any)
                              : ("" as string),
                          }}
                        />
                      </Box>
                      <Box width={"50%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            "Term Date": item?.deaTermDate
                              ? (formatDate(item?.deaTermDate) as any)
                              : ("" as string),
                          }}
                        />
                      </Box>
                    </Flex>
                  )
                )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </OutlineCard>
      <OutlineCard>
        <Box mx={"16px"}>
          <Accordion
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              sx={{ paddingLeft: 0 }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`licensed-info-content`}
              id={`licensed-info-header`}
            >
              <Typography fontWeight={500}>Licensed Information</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {providerData?.licenceInformation &&
                providerData?.licenceInformation?.length > 0 &&
                providerData?.licenceInformation?.map(
                  (item: any, index: number) => (
                    <Flex mt={"27px"} key={index}>
                      <Box width={"33%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            Number: item?.licenseNumber ?? "",
                          }}
                        />
                      </Box>
                      <Box width={"33%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            " State":
                              STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[
                                item?.licenseState ?? ""
                              ],
                          }}
                        />
                      </Box>
                      <Box width={"33%"}>
                        <ProfileViewer
                          gap={"5px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            "Active Date": item?.licenseActiveDate
                              ? (formatDate(item?.licenseActiveDate) as any)
                              : ("" as string),
                          }}
                        />
                      </Box>
                      <Box width={"33%"}>
                        <ProfileViewer
                          gap={"14px"}
                          withoutColn
                          firstColW={3.5}
                          secondColW={8}
                          data={{
                            "Term Date": item?.expiryDate
                              ? (formatDate(item?.expiryDate) as any)
                              : ("" as string),
                          }}
                        />
                      </Box>
                      <Box width={"10%"}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            previewResultDisc?.open(item?.licenseDocument)
                          }
                        >
                          <DownloadIcon />
                        </Button>
                      </Box>
                    </Flex>
                  )
                )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </OutlineCard>

      <PdfViewDrawer
        w="65vw"
        file={previewResultDisc?.data}
        disclosureHost={previewResultDisc}
        title="Licensed Document"
      />
    </Flex>
  );
};

export default ProviderProfile;
